import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { SkeletonCard } from '../components/ui';
import { listQuarterlyReportSummaries } from '../services/quarterlyReportService';
import type { QuarterlyReportSummary } from '../types/quarterlyReport';
import { LoadFailureCard, PageHeader, UploadIcon } from '../components/layout/memberPrimitives';
import { settle } from '../utils/dashboard/settle';
import type { SourceStatus } from '../utils/dashboard/types';

interface UploadedDocSummary {
  id: string;
  fileName: string;
  uploadedAt?: any;
}

function formatDate(value?: any): string {
  if (!value) return '';
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function ReportsPage() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState<QuarterlyReportSummary[]>([]);
  const [uploads, setUploads] = useState<UploadedDocSummary[]>([]);
  // Tracked separately per source: the two reads are independent, so one
  // failing must not blank the other's section.
  const [reportsStatus, setReportsStatus] = useState<SourceStatus>('empty');
  const [uploadsStatus, setUploadsStatus] = useState<SourceStatus>('empty');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const load = async () => {
      if (!currentUser) return;
      setLoading(true);
      // This was a try/finally with no catch. A denied or failed read left both
      // arrays empty and the page confidently told the member their record was
      // empty — see utils/dashboard/settle.
      const [reportResult, docsResult] = await Promise.all([
        settle(listQuarterlyReportSummaries(currentUser.uid)),
        settle(
          getDocs(
            query(
              collection(db, 'users', currentUser.uid, 'medical_documents'),
              orderBy('uploadedAt', 'desc'),
            ),
          ),
          (snap) => snap.empty,
        ),
      ]);

      setReportsStatus(reportResult.status);
      setReports(reportResult.data ?? []);

      setUploadsStatus(docsResult.status);
      setUploads(
        (docsResult.data?.docs ?? []).map((document) => {
          const data = document.data() as Record<string, any>;
          return {
            id: document.id,
            fileName: data.fileName || data.name || 'Untitled file',
            uploadedAt: data.uploadedAt,
          };
        }),
      );
      setLoading(false);
    };
    load();
  }, [currentUser, reloadToken]);

  const retry = () => setReloadToken((token) => token + 1);

  // The header needs no data, so it renders straight away rather than the whole
  // content region blanking to a centred spinner on every navigation.
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-10">
        <PageHeader
          breadcrumb={[{ label: 'Home', href: '/home' }, { label: 'Reports' }]}
          eyebrow="Your medical record"
          title="Reports & documents"
          subtitle="Everything Melio prepares for you, and anything you've shared with us."
        />
        <div className="grid lg:grid-cols-2 gap-6">
          <SkeletonCard lines={3} />
          <SkeletonCard lines={3} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-10 melio-stagger">
      <PageHeader
        breadcrumb={[{ label: 'Home', href: '/home' }, { label: 'Reports' }]}
        eyebrow="Your medical record"
        title="Reports & documents"
        subtitle="Everything Melio prepares for you, and anything you've shared with us."
        rightSlot={
          <Link
            to="/results"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border melio-lift"
            style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white', color: 'var(--melio-ink)' }}
          >
            <UploadIcon /> Upload document
          </Link>
        }
      />

      <div className="grid lg:grid-cols-2 gap-6">
        <section
          className="rounded-3xl border p-6 md:p-8"
          style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
        >
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--melio-sage)' }}>From Melio</p>
              <h2 className="text-xl md:text-2xl font-normal" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
                Quarterly reports
              </h2>
            </div>
            <Link
              to="/reports/sample"
              className="text-xs underline"
              style={{ color: 'var(--melio-text-muted)' }}
            >
              View sample
            </Link>
          </div>

          {reportsStatus === 'failed' ? (
            <LoadFailureCard
              title="We couldn’t load your reports"
              body="Your reports are safe — this is a connection problem, not a change to your record."
              onRetry={retry}
            />
          ) : reports.length === 0 ? (
            <div
              className="rounded-2xl p-5"
              style={{ backgroundColor: 'rgba(79, 93, 24, 0.08)', border: '1px dashed var(--melio-border)' }}
            >
              <p className="text-sm mb-3" style={{ color: 'var(--melio-charcoal)' }}>
                Your first quarterly report will appear here after your next clinician review.
              </p>
              <p className="text-sm mb-4" style={{ color: 'var(--melio-text-muted)' }}>
                Each report summarises what changed in the quarter, what your clinician recommends now, and what&rsquo;s scheduled next. Want a preview?
              </p>
              <Link
                to="/reports/sample"
                className="inline-block px-4 py-2 rounded text-sm font-medium"
                style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
              >
                View sample report
              </Link>
            </div>
          ) : (
            <ul className="space-y-3">
              {reports.map((report) => (
                <li key={report.id}>
                  <Link
                    to={`/reports/${report.id}`}
                    className="block rounded-2xl border p-4 transition-colors hover:opacity-90"
                    style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--melio-cream-warm)' }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>{report.quarterLabel}</p>
                          <span
                            className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: 'rgba(79, 93, 24, 0.12)', color: 'var(--melio-sage)' }}
                          >
                            Released
                          </span>
                        </div>
                        <p className="text-sm line-clamp-2" style={{ color: 'var(--melio-text-muted)' }}>
                          {report.headline}
                        </p>
                        <p className="text-xs mt-2" style={{ color: 'var(--melio-text-muted)' }}>
                          {report.signingClinician
                            ? `Reviewed by ${report.reviewingClinician} · signed off by ${report.signingClinician}`
                            : `Reviewed by ${report.reviewingClinician}`}
                          {report.releasedAt ? ` · ${formatDate(report.releasedAt)}` : ''}
                        </p>
                      </div>
                      <span aria-hidden style={{ color: 'var(--melio-text-muted)' }}>→</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section
          className="rounded-3xl border p-6 md:p-8"
          style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
        >
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--melio-sage)' }}>Shared by you</p>
              <h2 className="text-xl md:text-2xl font-normal" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
                Your documents
              </h2>
            </div>
            <Link
              to="/results"
              className="text-xs underline"
              style={{ color: 'var(--melio-text-muted)' }}
            >
              Upload or manage →
            </Link>
          </div>

          {uploadsStatus === 'failed' ? (
            <LoadFailureCard
              title="We couldn’t load your documents"
              body="Anything you have shared with us is still on file — this is a connection problem."
              onRetry={retry}
            />
          ) : uploads.length === 0 ? (
            <div
              className="rounded-2xl p-5"
              style={{ backgroundColor: 'rgba(79, 93, 24, 0.08)', border: '1px dashed var(--melio-border)' }}
            >
              <p className="text-sm mb-3" style={{ color: 'var(--melio-charcoal)' }}>
                No documents on file yet.
              </p>
              <p className="text-sm mb-4" style={{ color: 'var(--melio-text-muted)' }}>
                Past lab reports, imaging, letters from other clinicians — upload anything that gives us context before your next review.
              </p>
              <Link
                to="/results"
                className="inline-block px-4 py-2 rounded text-sm font-medium border"
                style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-charcoal)' }}
              >
                Upload a document
              </Link>
            </div>
          ) : (
            <ul className="space-y-2">
              {uploads.slice(0, 8).map((upload) => (
                <li
                  key={upload.id}
                  className="rounded-xl border px-4 py-3 flex items-center justify-between gap-3"
                  style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--melio-cream-warm)' }}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--melio-charcoal)' }}>{upload.fileName}</p>
                    <p className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>{formatDate(upload.uploadedAt) || 'Uploaded'}</p>
                  </div>
                </li>
              ))}
              {uploads.length > 8 && (
                <li>
                  <Link
                    to="/results"
                    className="text-xs underline"
                    style={{ color: 'var(--melio-text-muted)' }}
                  >
                    Show all {uploads.length} documents →
                  </Link>
                </li>
              )}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
